import React, {Fragment, useState} from "react";
import {useNavigate} from "react-router-dom";
import {createWithdrawal} from "../api/apiClient";

const inputClassName = "w-full border border-gray-300 rounded-md p-2 mt-1 focus:outline-none focus:ring-pegasusPurple focus:border-pegasusPurple";
const primaryButtonClassName = "w-full bg-pegasusPurple text-white py-2 px-4 rounded-md hover:bg-pegasusPurpleHover disabled:opacity-50";

const requiredFieldMessages = {
    name              : "Der Name ist erforderlich.",
    contractReference : "Bitte gib deine Bestell- oder Rechnungsnummer an.",
    email             : "Die E-Mail ist erforderlich.",
};

const formatReceivedAt = (receivedAt) => {
    const date = new Date(receivedAt);
    const day = date.toLocaleDateString("de-DE", {day : "2-digit", month : "2-digit", year : "numeric", timeZone : "Europe/Berlin"});
    const time = date.toLocaleTimeString("de-DE", {hour : "2-digit", minute : "2-digit", timeZone : "Europe/Berlin"});

    return `${day} ${time} Uhr`;
};

// The API client packs the HTTP status into the error message; a network failure has no status at all.
const parseApiError = (error) => {
    try {
        return JSON.parse(error.message);
    } catch {
        return {status : null};
    }
};

const FieldError =({errors, field}) => errors[field]
    ? <p className="text-red-500 text-sm mt-1">{errors[field][0]}</p>
    : null;

// § 356a BGB requires the withdrawal to be confirmed in a second step before it is sent.
const WithdrawalPage = () => {
    const [step, setStep] = useState("form");
    const [withdrawal, setWithdrawal] = useState({name : "", contractReference : "", email : "", reason : ""});
    const [errors, setErrors] = useState({});
    const [errorMessage, setErrorMessage] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [receivedAt, setReceivedAt] = useState(null);
    const navigate = useNavigate();

    const handleChange = (e) => {
        setWithdrawal({...withdrawal, [e.target.name] : e.target.value});
    };

    const handleWithdraw = (e) => {
        e.preventDefault();

        const validationErrors = {};
        Object.keys(requiredFieldMessages)
            .filter((field) => !withdrawal[field].trim())
            .forEach((field) => validationErrors[field] = [requiredFieldMessages[field]]);

        setErrors(validationErrors);
        setErrorMessage(null);
        if (Object.keys(validationErrors).length === 0) {
            setStep("confirm");
        }
    };

    const handleConfirm = () => {
        setIsSending(true);
        setErrorMessage(null);

        createWithdrawal(withdrawal.name, withdrawal.contractReference, withdrawal.email, withdrawal.reason)
            .then((response) => {
                setReceivedAt(response.receivedAt);
                setStep("received");
            })
            .catch((error) => {
                const response = parseApiError(error);

                if (response.status === 422) {
                    setErrors(response.data.errors);
                    setStep("form");
                } else if (response.status === 429) {
                    setErrorMessage("Bitte versuche es in einer Minute erneut.");
                } else {
                    setErrorMessage("Etwas ist schief gelaufen. Bitte versuche es erneut.");
                }
            })
            .finally(() => setIsSending(false));
    };

    return (
        <Fragment>
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center">
                <div className="w-full max-w-md bg-white shadow-lg rounded-lg p-6">
                    {step === "form" && (
                        <>
                            <h2 className="text-2xl font-semibold text-gray-800 text-center mb-4">
                                Vertrag widerrufen
                            </h2>
                            <p className="text-gray-600 text-center mb-6">
                                Hier kannst du deinen Kauf bei uns widerrufen.
                                Im nächsten Schritt kannst du deine Angaben noch einmal prüfen.
                            </p>

                            <form onSubmit={handleWithdraw} noValidate>
                                <div className="mb-4">
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                        Name*
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={withdrawal.name}
                                        onChange={handleChange}
                                        maxLength={255}
                                        className={inputClassName}
                                        placeholder="Dein Name"
                                    />
                                    <FieldError errors={errors} field="name"/>
                                </div>
                                <div className="mb-4">
                                    <label htmlFor="contractReference" className="block text-sm font-medium text-gray-700">
                                        Bestell- oder Rechnungsnummer*
                                    </label>
                                    <input
                                        type="text"
                                        id="contractReference"
                                        name="contractReference"
                                        value={withdrawal.contractReference}
                                        onChange={handleChange}
                                        maxLength={255}
                                        className={inputClassName}
                                        placeholder="z. B. aus deiner Bestellbestätigung oder Rechnung"
                                    />
                                    <FieldError errors={errors} field="contractReference"/>
                                </div>
                                <div className="mb-4">
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                        E-Mail*
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={withdrawal.email}
                                        onChange={handleChange}
                                        maxLength={255}
                                        className={inputClassName}
                                        placeholder="Deine E-Mail"
                                    />
                                    <FieldError errors={errors} field="email"/>
                                </div>
                                <div className="mb-6">
                                    <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                                        Grund des Widerrufs
                                    </label>
                                    <textarea
                                        id="reason"
                                        name="reason"
                                        value={withdrawal.reason}
                                        onChange={handleChange}
                                        maxLength={2000}
                                        rows={4}
                                        className={inputClassName}
                                        placeholder="Optional"
                                    />
                                    <FieldError errors={errors} field="reason"/>
                                </div>
                                <button type="submit" className={primaryButtonClassName}>
                                    Vertrag widerrufen
                                </button>
                            </form>
                        </>
                    )}

                    {step === "confirm" && (
                        <>
                            <h2 className="text-2xl font-semibold text-gray-800 text-center mb-4">
                                Bitte prüfe deine Angaben
                            </h2>
                            <dl className="text-gray-700 mb-6 space-y-3">
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Name</dt>
                                    <dd>{withdrawal.name}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Bestell- oder Rechnungsnummer</dt>
                                    <dd>{withdrawal.contractReference}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">E-Mail</dt>
                                    <dd>{withdrawal.email}</dd>
                                </div>
                                {withdrawal.reason.trim() && (
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Grund des Widerrufs</dt>
                                        <dd className="whitespace-pre-line break-words">{withdrawal.reason}</dd>
                                    </div>
                                )}
                            </dl>

                            {errorMessage && <p className="text-red-500 mb-4">{errorMessage}</p>}

                            <button
                                type="button"
                                onClick={handleConfirm}
                                disabled={isSending}
                                className={primaryButtonClassName}
                            >
                                Widerruf bestätigen
                            </button>
                            <button
                                type="button"
                                onClick={() => setStep("form")}
                                disabled={isSending}
                                className="w-full mt-3 text-pegasusPurple hover:text-pink-700 font-medium"
                            >
                                Angaben ändern
                            </button>
                        </>
                    )}

                    {step === "received" && (
                        <div className="text-center">
                            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                                Wir haben deinen Widerruf erhalten
                            </h2>
                            <p className="text-gray-600 mb-2">
                                Eingegangen am {formatReceivedAt(receivedAt)}.
                            </p>
                            <p className="text-gray-600 mb-6">
                                Eine Eingangsbestätigung haben wir dir an {withdrawal.email} geschickt.
                            </p>
                            <button
                                onClick={() => navigate("/")}
                                className="bg-pegasusPurple text-white py-2 px-6 rounded-lg hover:bg-pegasusPurpleHover"
                            >
                                ZURÜCK ZUM KALENDER
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </Fragment>
    );
};

export default WithdrawalPage;
